import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import {
  needsDateOfBirthWarning,
  ParticipantCellProps,
  ParticipantPrimaryRowBand,
  useParticipantRowModel,
} from './shared';

function ParticipantDetailLine({
  label,
  value,
  sx,
}: {
  label: string;
  value: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, minWidth: 0, ...sx }}>
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}:
      </Typography>
      <Typography variant="caption" noWrap title={value}>
        {value}
      </Typography>
    </Box>
  );
}

function ParticipantNameCell({ participant, callbacks }: ParticipantCellProps) {
  const { displayName, t } = useParticipantRowModel(participant, callbacks);
  const showMissingDateOfBirth = needsDateOfBirthWarning(participant, callbacks);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, py: 0.25 }}>
      <Typography variant="body2" noWrap title={displayName}>
        {displayName}
      </Typography>
      {showMissingDateOfBirth ? (
        <Typography variant="caption" color="error" component="p" sx={{ m: 0, lineHeight: 1.3 }}>
          {t('userCheckinWizard.missingDateOfBirth')}
        </Typography>
      ) : null}
    </Box>
  );
}

function ParticipantDetailsPanel({ participant }: { participant: ParticipantCellProps['participant'] }) {
  const { t } = useTranslation();
  const { email, city, gender } = participant;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.5,
        columnGap: 2,
        width: '100%',
        flexShrink: 0,
        py: 0.5,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <ParticipantDetailLine
        label={t('form.email')}
        value={email || '-'}
        sx={{ gridColumn: '1 / -1' }}
      />
      <ParticipantDetailLine label={t('form.city')} value={city || '-'} />
      <ParticipantDetailLine label={t('form.gender')} value={t(`base.gender.${gender}`)} />
    </Box>
  );
}

export function ParticipantNameWithDetailsCell({
  participant,
  callbacks,
  showDetails,
}: ParticipantCellProps & { showDetails: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <ParticipantPrimaryRowBand isExpanded={showDetails}>
        <ParticipantNameCell participant={participant} callbacks={callbacks} />
      </ParticipantPrimaryRowBand>
      {showDetails ? <ParticipantDetailsPanel participant={participant} /> : null}
    </Box>
  );
}

export function ParticipantExpandToggle({
  expanded,
  isExpanded,
  onToggle,
}: {
  expanded: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
      <ParticipantPrimaryRowBand isExpanded={isExpanded}>
        <IconButton aria-label="expand row" size="small" onClick={onToggle}>
          {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </ParticipantPrimaryRowBand>
    </Box>
  );
}
